import { getLinkPreviews, type BaseMessage, type LinkPreview } from "@fitness-app/data-models/entities/Chat";
import type { TFunction } from "i18next";

/**
 * Presentation rules for the link cards under a chat message. Shared so the three renderers
 * (admin, client-web, mobile) agree on which previews to show, how wide a card is and which
 * layout an image gets; each app keeps only the markup.
 */

/** Wider than a media bubble: a card carries a title and two lines of text, not one picture. */
export const LINK_PREVIEW_CARD_MAX_WIDTH = 360;

/** Share of a phone's width a card may take, so it still reads as a message, not a page. */
const VIEWPORT_WIDTH_RATIO = 0.82;

/** A hero image needs to be wide enough to carry the card; narrower ones become a thumbnail. */
const HERO_MIN_WIDTH = 200;
const HERO_MIN_ASPECT_RATIO = 1.3;

export type LinkPreviewLayout = "hero" | "thumbnail" | "text";

export const getLinkPreviewCardWidth = (viewportWidth?: number): number =>
  viewportWidth && viewportWidth > 0
    ? Math.min(LINK_PREVIEW_CARD_MAX_WIDTH, Math.round(viewportWidth * VIEWPORT_WIDTH_RATIO))
    : LINK_PREVIEW_CARD_MAX_WIDTH;

/**
 * Previews with anything to put on a card, for links still present in the text. Previews are
 * resolved once, on insert; an edit that drops or swaps a URL must not keep showing the old card
 * (a URL added by an edit gets no card — the trade-off of resolving once).
 */
export const getRenderableLinkPreviews = (
  message: Pick<BaseMessage, "extra" | "content"> | null | undefined,
): LinkPreview[] =>
  getLinkPreviews(message).filter(
    (preview) =>
      Boolean(preview.title || preview.description || preview.imageUrl) &&
      (message?.content ?? "").includes(preview.url),
  );

/**
 * Which slot the image takes. Landscape images wide enough to fill the card go on top (the
 * "summary_large_image" look); portrait, square, or small ones sit beside the text. Without
 * dimensions the page type decides: videos and articles ship wide covers, other sites rarely do.
 */
export const getLinkPreviewLayout = (preview: LinkPreview): LinkPreviewLayout => {
  if (!preview.imageUrl || preview.kind === "tweet") return "text";

  const { imageWidth, imageHeight } = preview;
  if (imageWidth && imageHeight) {
    return imageWidth >= HERO_MIN_WIDTH && imageWidth / imageHeight >= HERO_MIN_ASPECT_RATIO ? "hero" : "thumbnail";
  }

  return preview.kind === "video" || preview.kind === "article" ? "hero" : "thumbnail";
};

/** Open Graph's recommended cover is 1200×630; the guess when a page declares no dimensions. */
const DEFAULT_HERO_ASPECT_RATIO = 1.91;

/** Width ÷ height of the hero slot, clamped so a very wide banner does not become a sliver. */
export const getLinkPreviewHeroAspectRatio = (preview: LinkPreview): number => {
  const { imageWidth, imageHeight } = preview;
  const ratio = imageWidth && imageHeight ? imageWidth / imageHeight : DEFAULT_HERO_ASPECT_RATIO;
  return Math.min(Math.max(ratio, 1.3), 2.4);
};

/** Height of the hero slot for a card that is exactly `cardWidth` wide (native lays out in points). */
export const getLinkPreviewHeroHeight = (preview: LinkPreview, cardWidth: number): number =>
  Math.round(cardWidth / getLinkPreviewHeroAspectRatio(preview));

const stripWww = (hostname: string): string => hostname.replace(/^www\./, "");

/** The line above the title: site name when the page declares one, else its bare host. */
export const getLinkPreviewSiteLabel = (preview: LinkPreview): string => {
  if (preview.siteName) return preview.siteName;
  try {
    return stripWww(new URL(preview.resolvedUrl || preview.url).hostname);
  } catch {
    return preview.url;
  }
};

/**
 * The card opens what the author pasted. `resolvedUrl` comes partly from the page's own `og:url`,
 * and a page may point that anywhere: Loom's share page names its home page there, so the card
 * led away from the video. The browser follows any redirect by itself.
 */
export const getLinkPreviewHref = (preview: LinkPreview): string => preview.url || preview.resolvedUrl;

/** The message's `extra` without the card of `url` — what the author's "remove preview" writes. */
export const withoutLinkPreview = (extra: BaseMessage["extra"], url: string): NonNullable<BaseMessage["extra"]> => ({
  ...extra,
  version: extra?.version ?? 1,
  linkPreviews: getLinkPreviews({ extra }).filter((preview) => preview.url !== url),
});

/** Tweet cards show "Name @handle"; the handle is omitted when it is all we know. */
export const getTweetAuthorLine = (preview: LinkPreview): { name: string; handle: string | null } | null => {
  if (preview.kind !== "tweet") return null;
  const name = preview.author?.name || preview.title || "";
  const handle = preview.author?.handle ?? null;
  if (!name && !handle) return null;
  return { name: name || `@${handle}`, handle: name && handle && name !== handle ? `@${handle}` : null };
};

export const getLinkPreviewAccessibilityLabel = (tUi: TFunction, preview: LinkPreview): string =>
  tUi<string>("uiCommon:open-link-value", { value0: preview.title || getLinkPreviewSiteLabel(preview) });
